import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useRef, type PropsWithChildren, type ReactNode } from "react";

interface ParallaxProps extends PropsWithChildren {
  outro?: ReactNode;
  intro?: ReactNode;
  frame?: ReactNode;
}
const Parallax = ({ intro, children, outro, frame }: ParallaxProps) => {
  const bgRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  useGSAP(
    () => {
      const contentEl = contentRef?.current;
      const bgEl = bgRef?.current;
      if (!bgEl || !contentEl) {
        return;
      } else {
        const hd = contentEl.scrollHeight - bgEl.offsetHeight;
        if (hd > 0) {
          gsap
            .timeline({
              scrollTrigger: {
                trigger: bgEl,
                start: "top top",
                end: "+=300%",
                pin: true,
                pinSpacing: true,
                scrub: true,
              },
            })
            .to(contentEl, { translateY: `-${hd}px` }, ">");
        }
      }
    },
    { dependencies: [contentRef, bgRef] },
  );
  return (
    <div
      ref={bgRef}
      className="flex flex-col w-full h-screen relative gap-0 overflow-hidden bg-[#111212]"
    >
      <div
        data-name-background-el
        className="absolute w-full h-full inset-0 z-1"
      >
        <div className="w-full h-full bg-[#111212]">{children}</div>
      </div>
      <div
        ref={contentRef}
        className="absolute w-full min-h-fit h-full top-0 left-0  z-2"
      >
        <div className="w-full h-full flex flex-col">{intro}</div>
        <div
          data-name-frame
          className="relative w-full h-screen md:h-[120vh] z-2"
        >
          <div className="w-full h-full flex relative">
            <div className="w-full h-1/2 absolute top-0 left-0 bg-linear-to-b from-[#111212]/70 to-transparent "></div>
            <div className="w-full h-1/2 absolute bottom-0 left-0 bg-linear-to-t from-[#111212]/70 to-transparent "></div>
            {frame}
            <div className="absolute -top-1 bg-[#111212]  w-full h-6"></div>
            <div className="absolute bg-[#111212]  inset-x-0 left-0   h-full w-6"></div>
            <div className="absolute bottom-0 bg-[#111212]  w-full h-6"></div>
            <div className="absolute  bg-[#111212]  right-0 h-full w-6"></div>
            <div className="pointer-events-none w-[calc(100%-38px)] h-[calc(100%-38px)] top-1/2 left-1/2 -translate-1/2 border-[#111212] absolute inset-0 rounded-2xl border-8 ">
              <div className="absolute top-0 left-0 -translate-[10px]">
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="block rotate-180"
                >
                  <path
                    d="M 40 40 V 0 C 40 39 40 40 0 40 H 40 Z"
                    fill="#111212"
                  />
                </svg>
              </div>
              <div className="absolute top-0 right-0 -translate-y-[10px] translate-x-[10px]">
                <svg
                  width="45"
                  height="45"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="block  -rotate-90"
                >
                  <path
                    d="M 40 40 V 0 C 40 39 40 40 0 40 H 40 Z"
                    fill="#111212"
                  />
                </svg>
              </div>
              <div className="absolute bottom-0 right-0 translate-y-[10px] translate-x-[10px]">
                <svg
                  width="45"
                  height="45"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="block  rotate-"
                >
                  <path
                    d="M 40 40 V 0 C 40 39 40 40 0 40 H 40 Z"
                    fill="#111212"
                  />
                </svg>
              </div>
              <div className="absolute bottom-0 left-0 translate-y-[10px] -translate-[10px]">
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="block rotate-90"
                >
                  <path
                    d="M 40 40 V 0 C 40 39 40 40 0 40 H 40 Z"
                    fill="#111212"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full min-h-fit flex flex-col h-fit">{outro}</div>
      </div>
    </div>
  );
};

export default Parallax;
